import { api } from './api';

export interface SessionInfo {
  userId: number;
  username: string;
  isActive: boolean;
  expiresAt: string;
  daysUntilExpiry: number;
}

export interface SessionRenewalResponse {
  message: string;
  token: string;
  expiresAt: string;
}

class SessionService {
  /**
   * 현재 세션 상태를 확인합니다.
   */
  async getSessionStatus(): Promise<SessionInfo> {
    try {
      console.log('🔍 [세션 서비스] 세션 상태 확인 요청');
      
      const response = await api.get('/session/status');
      
      if (response.data) {
        console.log('✅ [세션 서비스] 세션 상태 확인 성공:', response.data);
        return response.data;
      } else {
        throw new Error('세션 상태 정보를 받을 수 없습니다.');
      }
    } catch (error: any) {
      console.error('❌ [세션 서비스] 세션 상태 확인 실패:', error);
      throw new Error(error.response?.data?.message || '세션 상태를 확인할 수 없습니다.');
    }
  }

  /**
   * 세션을 갱신합니다.
   */
  async renewSession(): Promise<SessionRenewalResponse> {
    try {
      console.log('🔄 [세션 서비스] 세션 갱신 요청');
      
      const response = await api.post('/session/renew');
      
      if (response.data && response.data.token) {
        console.log('✅ [세션 서비스] 세션 갱신 성공');
        
        // 새 토큰을 로컬 스토리지에 저장
        localStorage.setItem('token', response.data.token);
        
        // 사용자 정보 업데이트
        this.updateUserLastLogin();
        
        return response.data;
      } else {
        throw new Error('세션 갱신 응답이 올바르지 않습니다.');
      }
    } catch (error: any) {
      console.error('❌ [세션 서비스] 세션 갱신 실패:', error);
      throw new Error(error.response?.data?.message || '세션 갱신에 실패했습니다.');
    }
  }

  /**
   * 사용자의 마지막 로그인 시간을 업데이트합니다.
   */
  private updateUserLastLogin(): void {
    try {
      const currentUser = localStorage.getItem('currentUser');
      if (currentUser) {
        const user = JSON.parse(currentUser);
        user.lastLoginAt = new Date().toISOString();
        localStorage.setItem('currentUser', JSON.stringify(user));
        console.log('✅ [세션 서비스] 사용자 정보 업데이트 완료');
      }
    } catch (error) {
      console.error('❌ [세션 서비스] 사용자 정보 업데이트 실패:', error);
    }
  }

  /**
   * 세션이 만료되었는지 확인합니다.
   */
  isSessionExpired(): boolean {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return true;
      }

      // JWT 토큰 디코딩하여 만료 시간 확인
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // JavaScript Date는 밀리초 단위
      const now = Date.now();
      
      return now >= exp;
    } catch (error) {
      console.error('❌ [세션 서비스] 토큰 만료 확인 실패:', error);
      return true;
    }
  }

  /**
   * 세션이 곧 만료될 예정인지 확인합니다 (1일 이내).
   */
  isSessionExpiringSoon(): boolean {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return false;
      }

      // JWT 토큰 디코딩하여 만료 시간 확인
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // JavaScript Date는 밀리초 단위
      const now = Date.now();
      const oneDayInMs = 24 * 60 * 60 * 1000; // 1일을 밀리초로
      
      const timeUntilExpiry = exp - now;
      const isExpiringSoon = timeUntilExpiry <= oneDayInMs && timeUntilExpiry > 0;
      
      console.log(`🔍 [세션 서비스] 토큰 만료 확인: ${new Date(exp).toISOString()}, 현재: ${new Date(now).toISOString()}, 남은 시간: ${Math.floor(timeUntilExpiry / (24 * 60 * 60 * 1000))}일`);
      
      return isExpiringSoon;
    } catch (error) {
      console.error('❌ [세션 서비스] 토큰 만료 예정 확인 실패:', error);
      return false;
    }
  }

  /**
   * 세션 만료까지 남은 일수를 계산합니다.
   */
  getDaysUntilExpiry(): number {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return 0;
      }

      // JWT 토큰 디코딩하여 만료 시간 확인
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // JavaScript Date는 밀리초 단위
      const now = Date.now();
      const daysUntilExpiry = Math.ceil((exp - now) / (24 * 60 * 60 * 1000));
      
      return Math.max(0, daysUntilExpiry);
    } catch (error) {
      console.error('❌ [세션 서비스] 만료일 계산 실패:', error);
      return 0;
    }
  }
}

export default new SessionService();
